// src/components/desk/AgentPicker.jsx — pick ONE, MANY, or ALL colleagues.
//
// Extracted from ClientSharePanel on 2026-09-08, when the same picker was needed for
// PROPERTY sharing (owner: "same should go with property"). One definition, because the two
// halves of the product are the same act and every rule below was paid for on the client
// side — copying it would mean re-learning each one on the property side.
//
// ⚠️ THE `ALL` ROW IS ABOVE THE LIST AND LOOKS LIKE THE ROWS IT COMMANDS. It first shipped as
// a small right-aligned text link trailing the grey note, rendered perfectly, and could not be
// found — measured on the owner's own Desk, where it read `ყველა (14)` and was invisible
// because it was set in the same size and colour as the sentence beside it. A control that
// looks like prose is prose.
//
// ⚠️ `ALL` MEANS "everyone listed right now" — the FILTERED set when a search is typed, never
// the whole roster. That is why the count is on the label: a control that says "all" and a
// number that says how many are the same promise, and it can be checked.
//
// ⚠️ A tap TOGGLES. The commit button is disabled until something is picked, so it can never
// fire an empty share, and it carries the count — "share" and "share with 9 people" are not
// the same act, and the second should have to be read.
import { useEffect, useRef, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { btn } from './primitives.jsx'

const isActive = a => a && a.is_active !== false && !a.resign_date

export function AgentPicker({
  agents = [],
  // Party/agent ids that already hold it — offering them again would present "add" for a
  // state that is already true.
  exclude = [],
  busy = false,
  note = null,
  onCommit,               // (agents[]) => void
  onCancel,
}) {
  const [q, setQ] = useState('')
  const [sel, setSel] = useState(() => new Set())
  const boxRef = useRef(null)
  useEffect(() => { if (!busy) setSel(new Set()) }, [busy])

  // ── TWO MOBILE DEFECTS, BOTH OWNER-REPORTED WITH SCREENSHOTS (2026-09-08) ───────────────
  //
  // 1. `autoFocus` ON A PHONE OPENS THE KEYBOARD, which ate half the screen the moment the
  //    picker appeared — over the very list the agent had come to read, and over the Share
  //    button below it. Focusing a search box before the user has asked to search is a
  //    desktop habit; on touch it costs them the thing they opened.
  //    ⚠️ Detected by POINTER, not by width: a narrow desktop window still deserves the
  //    focus, and a wide tablet with a touch keyboard does not.
  // 2. The picker sits mid-document in a modal whose FOOTER is sticky, so its action row
  //    could open underneath that bar — visible, unreachable. Scrolling it into view on open
  //    puts the buttons in the middle of the viewport, clear of both the footer and (when it
  //    is present) the keyboard.
  const finePointer = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: fine)').matches
  useEffect(() => {
    const el = boxRef.current
    if (!el) return
    // `center` rather than `nearest`: nearest is satisfied by "already fully on screen",
    // which is exactly the state where the sticky footer is painted over it.
    try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }) } catch { /* older browsers */ }
  }, [])

  const ql = q.trim().toLowerCase()
  const held = new Set((exclude || []).filter(Boolean))
  // Active only: nothing can be shared with someone who has left the company.
  const roster = agents.filter(a => isActive(a) && !held.has(a.id)
    && (!ql || (a.name || '').toLowerCase().includes(ql)))
  const allOn = roster.length > 0 && roster.every(a => sel.has(a.id))

  return (
    <div ref={boxRef} className="rounded-lg border border-border p-2 space-y-1.5">
      <input autoFocus={finePointer} className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
        placeholder={t('desk_client_share_search')} value={q} onChange={e => setQ(e.target.value)} />
      {roster.length > 1 && (
        <button type="button" data-share-all disabled={busy}
          className={`w-full text-left rounded-md border border-dashed px-2 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-40 ${
            allOn ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-semibold'
                  : 'border-border text-foreground hover:bg-accent'}`}
          onClick={() => setSel(allOn ? new Set() : new Set(roster.map(a => a.id)))}>
          <span className="w-3 shrink-0">{allOn ? <Icon name="check" size={12} /> : null}</span>
          {allOn ? t('desk_client_share_none') : tLog('desk_client_share_all', { n: roster.length })}
        </button>
      )}
      {/* The note is a warning about the ACT, so it sits below the control rather than acting
          as its label — putting a button at the end of a warning is what made `All` invisible. */}
      {note && <p className="text-[11px] text-muted-foreground">{note}</p>}
      <div className="max-h-44 overflow-y-auto space-y-0.5">
        {roster.length === 0 && (
          <p className="text-xs text-muted-foreground px-1 py-1">{t('desk_client_share_nobody')}</p>
        )}
        {roster.slice(0, 40).map(a => {
          const on = sel.has(a.id)
          return (
            <button key={a.id} type="button" data-share-agent={a.id} data-selected={on || undefined} disabled={busy}
              className={`w-full text-left rounded-md px-2 py-1 text-xs flex items-center gap-1.5 disabled:opacity-40 ${
                on ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'hover:bg-accent'}`}
              onClick={() => setSel(s => { const n = new Set(s); n.has(a.id) ? n.delete(a.id) : n.add(a.id); return n })}>
              {/* A tick always in the layout, empty or full: a mark that appears only when
                  selected makes every row shift as it is picked. */}
              <span className="w-3 shrink-0">{on ? <Icon name="check" size={12} /> : null}</span>
              {a.name}
            </button>
          )
        })}
      </div>
      {/* ⚠️ STICKY INSIDE THE PICKER (owner screenshots, 2026-09-08). The list above is a
          scroller and the host modal's own footer is sticky, so on a phone the action row
          could end up painted under that bar — visible and unpressable. Sticking it to the
          bottom of the PANEL means it travels with the panel and is never the thing that
          falls off the end. `bg-background` is required, not decoration: a transparent
          sticky row would have the list scrolling through it. */}
      <div className="sticky bottom-0 -mx-2 -mb-2 px-2 py-2 bg-background border-t border-border/60 flex items-center gap-2">
        <button type="button" className={btn('primary')} data-share-commit
          disabled={busy || sel.size === 0}
          onClick={() => onCommit?.(roster.filter(a => sel.has(a.id)))}>
          {sel.size > 0 ? tLog('desk_client_share_commit_n', { n: sel.size }) : t('desk_client_share_commit')}
        </button>
        <button type="button" className={btn('ghost')} onClick={() => { setQ(''); setSel(new Set()); onCancel?.() }}>
          {t('cancel')}
        </button>
      </div>
    </div>
  )
}
